// What was wrong with a Neo answer (TOM-461).
//
// A thumbs-down is recorded at once, as before (ADR 0119). Then a small ask
// opens under the answer: one-tap reasons and an optional "What did you
// expect?" box. Sending posts the same rating again with the reason and the
// words; the server keeps the last post for each answer and owner. Closing the
// ask leaves the bare rating.
//
// Pure, so cloud/web's Node vitest covers it (no DOM harness).

export type NeoFeedbackRating = 'up' | 'down';

/** The reason codes the feedback route takes (`NeoFeedbackReason`, cloud/src/core/db/kysely.ts). */
export type NeoFeedbackReason = 'wrong' | 'unhelpful' | 'incomplete' | 'not_grounded' | 'other';

/** The reasons as the owner reads them, in the order the ask shows them. */
export const NEO_FEEDBACK_REASONS: readonly { code: NeoFeedbackReason; label: string }[] = [
  { code: 'wrong', label: 'Wrong' },
  { code: 'unhelpful', label: 'Not useful' },
  { code: 'incomplete', label: 'Missing something' },
  { code: 'not_grounded', label: 'Made something up' },
  { code: 'other', label: 'Something else' },
];

/** The owner's label for a stored reason code, or null for a code this client does not know. */
export function neoFeedbackReasonLabel(code: string): string | null {
  return NEO_FEEDBACK_REASONS.find((reason) => reason.code === code)?.label ?? null;
}

/** The longest comment the route takes (`FeedbackBody`, cloud/src/api/neo/routes.ts). */
export const NEO_FEEDBACK_COMMENT_MAX = 1000;

export const NEO_FEEDBACK_ASK_TITLE = 'What was wrong?';
export const NEO_FEEDBACK_ASK_PLACEHOLDER = 'What did you expect? (optional)';
export const NEO_FEEDBACK_ASK_HINT = 'The Neomaris team reads this to improve Neo.';
export const NEO_FEEDBACK_THANKS = 'Thanks. The Neomaris team will read this.';
export const NEO_FEEDBACK_SEND_FAILED = 'Couldn’t send. Check the connection and try again.';

/**
 * Where the ask under one answer stands. Absent: nothing shows.
 * - `open`: the ask shows under the 👎.
 * - `thanked`: the owner sent a reason; a thank-you line shows while the 👎 stands.
 * - `told`: the owner sent a reason, then changed the rating. Nothing shows, and
 *   the ask does not come back for that answer.
 */
export type NeoFeedbackAsk = 'open' | 'thanked' | 'told';

/**
 * The ask after the owner changes an answer's rating to `rating`. A 👎 asks
 * what was wrong, unless the owner already said for this answer. A 👍 closes
 * the ask: the server's rating no longer carries the reason.
 */
export function neoFeedbackAskAfterRating(
  ask: NeoFeedbackAsk | undefined,
  rating: NeoFeedbackRating
): NeoFeedbackAsk | undefined {
  if (ask === 'thanked' || ask === 'told') return 'told';
  return rating === 'down' ? 'open' : undefined;
}

/** What the owner chose in the ask: the body's extra fields, or null when there is nothing to send. */
export interface NeoFeedbackDetail {
  reasonCode?: NeoFeedbackReason;
  comment?: string;
}

/**
 * The reason and the comment as the route takes them, or null when the owner
 * gave neither. A blank comment is left out, and a long one is cut to the
 * route's limit (the box stops there too).
 */
export function neoFeedbackDetail(
  reason: NeoFeedbackReason | null,
  comment: string
): NeoFeedbackDetail | null {
  const words = comment.trim().slice(0, NEO_FEEDBACK_COMMENT_MAX).trim();
  if (reason === null && words.length === 0) return null;
  return {
    ...(reason === null ? {} : { reasonCode: reason }),
    ...(words.length === 0 ? {} : { comment: words }),
  };
}
